import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator,
  DropdownMenuTrigger 
} from '../components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { FileText, Home, User, LogOut, Crown } from 'lucide-react';
import { authFetch } from '../utils/api';

export const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [actualPremium, setActualPremium] = useState(false);

  // Check actual premium status from backend on mount and periodically
  useEffect(() => {
    if (!user) return;
    
    const checkPremium = async () => {
      try {
        const response = await authFetch('/users/premium-status');
        if (response.ok) {
          const data = await response.json();
          setActualPremium(data.is_premium === true);
        }
      } catch (error) {
        console.error('Failed to check premium:', error);
      }
    };
    
    checkPremium();
    // Recheck every 30 seconds to catch expiry
    const interval = setInterval(checkPremium, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border bg-white/80 backdrop-blur-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group" data-testid="logo-link">
            <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center group-hover:bg-primary/90 transition-colors">
              <FileText className="h-5 w-5 text-white" />
            </div>
            <span className="font-heading font-bold text-xl text-primary hidden sm:block">
              FixResume24
            </span>
          </Link>

          {/* Navigation */}
          {user && (
            <nav className="hidden md:flex items-center space-x-8">
              <Link 
                to="/" 
                className="text-muted-foreground hover:text-primary transition-colors font-medium"
                data-testid="nav-home"
              >
                Home
              </Link>
              <Link 
                to="/my-resumes" 
                className="text-muted-foreground hover:text-primary transition-colors font-medium"
                data-testid="nav-resumes"
              >
                My Resumes
              </Link>
            </nav>
          )}

          {/* User Menu */}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="relative h-10 w-10 rounded-full"
                  data-testid="user-menu-trigger"
                >
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={user.picture} alt={user.name} />
                    <AvatarFallback className="bg-primary text-white font-medium">
                      {getInitials(user.name)}
                    </AvatarFallback>
                  </Avatar>
                  {actualPremium && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-accent rounded-full flex items-center justify-center">
                      <Crown className="w-2.5 h-2.5 text-white" />
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium text-primary">{user.name}</p>
                  <p className="text-xs text-muted-foreground">{user.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/" className="cursor-pointer" data-testid="menu-home">
                    <Home className="mr-2 h-4 w-4" />
                    Home
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/my-resumes" className="cursor-pointer" data-testid="menu-my-resumes">
                    <FileText className="mr-2 h-4 w-4" />
                    My Resumes
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/profile" className="cursor-pointer" data-testid="menu-profile">
                    <User className="mr-2 h-4 w-4" />
                    Profile
                  </Link>
                </DropdownMenuItem>
                {!actualPremium && (
                  <DropdownMenuItem asChild>
                    <Link to="/upgrade" className="cursor-pointer text-accent" data-testid="menu-upgrade">
                      <Crown className="mr-2 h-4 w-4" />
                      Upgrade to Premium
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={handleLogout}
                  className="text-destructive cursor-pointer"
                  data-testid="menu-logout"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/login">
              <Button data-testid="login-btn">Sign In</Button>
            </Link>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
